import { cumulate, drawnSeries } from '../charts/ticketSalesSeries';

/** One row of `ticketSalesSeries`: a night's stage, counted per bucket on the shared "days before doors close" axis. */
export interface SalesSeriesRow {
  nightId: string;
  stageName: string;
  data: readonly number[];
  pending: number;
}

export type SalesCurveView = 'total' | 'stage';

export interface SalesCurve {
  /** The lines in drawing order: this night (one total, or one per stage), then the comparison as one total line, last. */
  lines: Array<{ name: string; data: Array<number | null>; comparison: boolean }>;
  /** This night's total sold at today's days-left minus the comparison's at the same days-left; null without a point to compare at. */
  vs: number | null;
}

/** The per-bucket sum of several rows, `length` buckets long. */
function sumRows(rows: readonly SalesSeriesRow[], length: number): number[] {
  const out = Array.from({ length }, () => 0);
  for (const row of rows) row.data.forEach((v, i) => (out[i] = (out[i] ?? 0) + v));
  return out;
}

/**
 * The Ticket sales card's curve from `ticketSalesSeries` for this night and its comparison night: cumulative tickets sold
 * against days left. Only this night's lines stop at today (`pending`); the comparison is one total line, drawn last and
 * in full, to where that night ended up. A comparison night with no rows sold nothing: a flat zero line, not a gap.
 * Vs reads the comparison's running total at the bucket this night's line ends on. A night with no sales yet has no series:
 * only the comparison line is drawn and Vs is null.
 */
export function buildSalesCurve(input: {
  rows: readonly SalesSeriesRow[];
  categories: readonly number[];
  nightId: string;
  compareId: string | null;
  compareName: string | null;
  view: SalesCurveView;
}): SalesCurve {
  const { rows, categories, nightId, compareId, compareName, view } = input;
  const length = categories.length;
  const mine = rows.filter((r) => r.nightId === nightId);
  const theirs = compareId ? rows.filter((r) => r.nightId === compareId) : [];
  const pending = mine[0]?.pending ?? 0;

  const lines: SalesCurve['lines'] = [];
  // Before this night's first sale it has no series (so no `pending` either): draw none of its lines, not a Total that would
  // run to doors close, and leave Vs out. The comparison line stays.
  if (view === 'stage') {
    for (const row of mine) {
      lines.push({ name: row.stageName, data: drawnSeries(row.data, row.pending, true), comparison: false });
    }
  } else if (mine.length > 0) {
    lines.push({ name: 'Total', data: drawnSeries(sumRows(mine, length), pending, true), comparison: false });
  }
  const theirTotal = sumRows(theirs, length);
  if (compareId && compareName !== null) {
    // Not cut at the comparison's own today: it is the yardstick, drawn to where that night ended up.
    lines.push({ name: compareName, data: cumulate(theirTotal), comparison: true });
  }

  const today = length - pending - 1;
  const vs = compareId && mine.length > 0 && today >= 0 ? (cumulate(sumRows(mine, length))[today] ?? 0) - (cumulate(theirTotal)[today] ?? 0) : null;
  return { lines, vs };
}

/**
 * ApexCharts marker size per line: a line with exactly one drawn point (a night that sold on one day only) has no segment to
 * draw, so it gets a visible marker; longer lines keep none.
 */
export function lineMarkerSizes(lines: ReadonlyArray<{ data: ReadonlyArray<number | null> }>): number[] {
  return lines.map((l) => (l.data.filter((v) => v !== null).length === 1 ? 5 : 0));
}

/** "+5 at this point", "-2 at this point", "level at this point". */
export function vsText(vs: number): string {
  if (vs === 0) return 'level at this point';
  return `${vs > 0 ? '+' : '−'}${Math.abs(vs)} at this point`;
}
